import { What3WordsInput } from '@/components/What3WordsInput';
import { createCustomer } from '@/lib/api';
import type { CreateCustomerInput } from '@/lib/types';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Loose email format check (RFC 5321 simplified).
function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

// ---------------------------------------------------------------------------
// Shared form field component
// ---------------------------------------------------------------------------

function Field({
  id,
  label,
  optional,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        {label}
        {optional && (
          <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">
            (optional)
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';
const selectCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
  what3words: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  preferredContactMethod: string;
  marketingSource: string;
  internalNotes: string;
}

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postcode: '',
  country: '',
  what3words: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactRelationship: '',
  preferredContactMethod: '',
  marketingSource: '',
  internalNotes: '',
};

function toNullable(s: string): string | null {
  return s.trim() || null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CustomerNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  function set(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear email error when user edits email
    if (field === 'email') setEmailError(null);
  }

  function validate(): boolean {
    if (form.email.trim() && !isValidEmail(form.email)) {
      setEmailError('Enter a valid email address');
      return false;
    }
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) return;
    if (!validate()) return;
    setBusy(true);
    setError(null);
    try {
      const body: CreateCustomerInput = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: toNullable(form.email),
        phone: toNullable(form.phone),
        addressLine1: toNullable(form.addressLine1),
        addressLine2: toNullable(form.addressLine2),
        city: toNullable(form.city),
        region: toNullable(form.region),
        postcode: toNullable(form.postcode),
        country: toNullable(form.country),
        what3words: toNullable(form.what3words),
        emergencyContactName: toNullable(form.emergencyContactName),
        emergencyContactPhone: toNullable(form.emergencyContactPhone),
        emergencyContactRelationship: toNullable(form.emergencyContactRelationship),
        preferredContactMethod:
          (form.preferredContactMethod as 'email' | 'sms' | 'phone' | 'whatsapp' | '') || null,
        marketingSource: toNullable(form.marketingSource),
        internalNotes: toNullable(form.internalNotes),
      };
      const customer = await createCustomer(body);
      navigate(`/customers/${customer.id}`);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
      <Link
        to="/customers"
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
      >
        <ArrowLeft className="size-4" strokeWidth={1.75} />
        Customers
      </Link>

      <h1 className="mb-6 font-heading text-2xl font-medium tracking-tight">New customer</h1>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-8">
        {/* About this customer */}
        <section>
          <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
            About this customer
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="firstName" label="First name">
              <input
                id="firstName"
                type="text"
                required
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                autoFocus
                className={inputCls}
              />
            </Field>
            <Field id="lastName" label="Last name">
              <input
                id="lastName"
                type="text"
                required
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="email" label="Email" optional>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                className={`${inputCls}${emailError ? ' border-destructive ring-1 ring-destructive/30' : ''}`}
              />
              {emailError && <p className="mt-0.5 text-xs text-destructive">{emailError}</p>}
            </Field>
            <Field id="phone" label="Phone" optional>
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="preferredContactMethod" label="Preferred contact" optional>
              <select
                id="preferredContactMethod"
                value={form.preferredContactMethod}
                onChange={(e) => set('preferredContactMethod', e.target.value)}
                className={selectCls}
              >
                <option value="">No preference</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </Field>
            <Field id="marketingSource" label="How did they find us?" optional>
              <input
                id="marketingSource"
                type="text"
                value={form.marketingSource}
                onChange={(e) => set('marketingSource', e.target.value)}
                className={inputCls}
                placeholder="e.g. Google, referral, walk-in"
              />
            </Field>
          </div>
        </section>

        {/* Address */}
        <section>
          <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
            Address
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="addressLine1" label="Address line 1" optional>
              <input
                id="addressLine1"
                type="text"
                value={form.addressLine1}
                onChange={(e) => set('addressLine1', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="addressLine2" label="Address line 2" optional>
              <input
                id="addressLine2"
                type="text"
                value={form.addressLine2}
                onChange={(e) => set('addressLine2', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="city" label="City / Town" optional>
              <input
                id="city"
                type="text"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="region" label="County / State" optional>
              <input
                id="region"
                type="text"
                value={form.region}
                onChange={(e) => set('region', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="postcode" label="Postcode / ZIP" optional>
              <input
                id="postcode"
                type="text"
                value={form.postcode}
                onChange={(e) => set('postcode', e.target.value)}
                className={`${inputCls} uppercase`}
              />
            </Field>
            <Field id="country" label="Country" optional>
              <input
                id="country"
                type="text"
                value={form.country}
                onChange={(e) => set('country', e.target.value)}
                placeholder="GB"
                className={inputCls}
              />
            </Field>
            <Field id="what3words" label="what3words" optional>
              <What3WordsInput
                id="what3words"
                value={form.what3words}
                onChange={(v) => set('what3words', v)}
                country={form.country.trim() || null}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                The exact gate or door for pickups and dropoffs.
              </p>
            </Field>
          </div>
        </section>

        {/* Emergency contact */}
        <section>
          <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
            Emergency contact
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="emergencyContactName" label="Name" optional>
              <input
                id="emergencyContactName"
                type="text"
                value={form.emergencyContactName}
                onChange={(e) => set('emergencyContactName', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="emergencyContactPhone" label="Phone" optional>
              <input
                id="emergencyContactPhone"
                type="tel"
                value={form.emergencyContactPhone}
                onChange={(e) => set('emergencyContactPhone', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="emergencyContactRelationship" label="Relationship" optional>
              <input
                id="emergencyContactRelationship"
                type="text"
                value={form.emergencyContactRelationship}
                onChange={(e) => set('emergencyContactRelationship', e.target.value)}
                placeholder="e.g. Spouse, neighbour"
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Staff notes */}
        <section>
          <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
            Internal notes{' '}
            <span className="font-normal tracking-normal text-muted-foreground/70 text-xs normal-case">
              (staff-only, never shown to customers)
            </span>
          </h2>
          <textarea
            id="internalNotes"
            value={form.internalNotes}
            onChange={(e) => set('internalNotes', e.target.value)}
            rows={4}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </section>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        <div className="sticky bottom-0 -mx-4 border-t border-border bg-background/95 backdrop-blur px-4 py-3 md:-mx-8 md:px-8">
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy || !form.firstName.trim() || !form.lastName.trim()}
              className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-60"
            >
              {busy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
              {busy ? 'Saving…' : 'Create customer'}
            </button>
            <Link
              to="/customers"
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary press"
            >
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
